/**
 * Renders children only when an API key is present in localStorage.
 * Otherwise prompts the admin to paste one. v1 placeholder until
 * Cognito auth lands. Don't bake the key into the JS bundle.
 */
import React, { useEffect, useState } from "react";
import { clearApiKey, hasApiKey, setApiKey } from "../../../api/campaigns";

interface Props {
  children: React.ReactNode;
}

const ApiKeyGate: React.FC<Props> = ({ children }) => {
  const [ready, setReady] = useState(hasApiKey());
  const [input, setInput] = useState("");

  useEffect(() => {
    setReady(hasApiKey());
  }, []);

  if (ready) {
    return (
      <div>
        {children}
        <button
          onClick={() => {
            clearApiKey();
            setReady(false);
          }}
          className="fixed bottom-4 right-4 text-xs text-gray-400 hover:text-gray-600 underline"
          title="Clear stored API key"
        >
          forget API key
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="bg-white rounded-lg shadow-md p-8 max-w-md w-full">
        <h2 className="text-xl font-semibold mb-2">Admin API key required</h2>
        <p className="text-sm text-gray-600 mb-6">
          The campaign API is gated by <code>X-Api-Key</code>. Paste the
          key once — it's stored only in your browser's localStorage.
        </p>
        <input
          type="password"
          autoFocus
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && input.trim()) {
              setApiKey(input.trim());
              setReady(true);
            }
          }}
          placeholder="API key"
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 mb-3"
        />
        <button
          disabled={!input.trim()}
          onClick={() => {
            setApiKey(input.trim());
            setReady(true);
          }}
          className="w-full bg-purple-600 text-white py-2 rounded-md hover:bg-purple-700 disabled:bg-gray-300"
        >
          Save key
        </button>
        <p className="text-xs text-gray-400 mt-4">
          Get the key value from the Amplify stack output{" "}
          <code>API.WalletHttpApi.apiKeyId</code> via{" "}
          <code>aws apigateway get-api-key --include-value</code>.
        </p>
      </div>
    </div>
  );
};

export default ApiKeyGate;
